/**
 * Conversión de imágenes **en el navegador**, antes de subir (§9.3).
 *
 * Nada de procesar en el servidor: ahorra CPU, ancho de banda y complejidad. El móvil que hace la
 * foto es el que la reduce, y lo que viaja por la wifi del centro son 200 kB en lugar de ocho megas.
 *
 * **Se usa WebP y no AVIF a propósito.** `canvas.toBlob('image/webp')` es nativo en todos los
 * navegadores objetivo y no añade dependencias; codificar AVIF exigiría un módulo WASM de unos
 * 800 kB para ahorrar un 20 % sobre imágenes que ya pesan 200 kB. Con el presupuesto de
 * almacenamiento del centro, la diferencia es irrelevante.
 */

/** Lado mayor de la imagen final: suficiente para un proyector de 1080p, donde ocupa ~60 % del alto. */
export const MAX_EDGE = 1280;
/** Lado mayor de la miniatura del editor. */
export const THUMB_EDGE = 320;
export const QUALITY = 0.72;
export const THUMB_QUALITY = 0.6;

/** Lo que se acepta de entrada. Un archivo de 15 MB ya es una foto de móvil moderna sin recortar. */
export const MAX_INPUT_BYTES = 15 * 1024 * 1024;
/** Y lo que se acepta **después** de convertir: por encima, algo ha ido mal. */
export const MAX_OUTPUT_BYTES = 400 * 1024;
/** Cuota por profesor. */
export const QUOTA_BYTES = 500 * 1024 * 1024;

export type ConversionError =
  | 'images.error.tooLarge'
  | 'images.error.notAnImage'
  | 'images.error.decodeFailed'
  | 'images.error.stillTooLarge'
  | 'images.error.quotaExceeded';

export interface ConvertedImage {
  readonly blob: Blob;
  readonly thumbnail: Blob;
  readonly width: number;
  readonly height: number;
}

export type ConversionResult =
  | { readonly ok: true; readonly image: ConvertedImage }
  | { readonly ok: false; readonly reason: ConversionError };

/** Las dimensiones finales, conservando la proporción y **sin ampliar nunca**. */
export function fit(
  width: number,
  height: number,
  maxEdge: number,
): { width: number; height: number } {
  const mayor = Math.max(width, height);
  // Ampliar una imagen pequeña no añade información y sí peso: se deja como está.
  if (mayor <= maxEdge) return { width, height };

  const factor = maxEdge / mayor;
  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
  };
}

/** Lo que hace falta del navegador, inyectable para poder probar la lógica sin uno. */
export interface ImageEnvironment {
  readonly decode: (
    file: File,
  ) => Promise<{ width: number; height: number; source: CanvasImageSource }>;
  readonly encode: (
    source: CanvasImageSource,
    width: number,
    height: number,
    quality: number,
  ) => Promise<Blob | null>;
}

export const browserEnvironment: ImageEnvironment = {
  /**
   * Decodifica con `createImageBitmap`, que es lo que permite aceptar HEIC y GIF sin código propio:
   * si el navegador sabe enseñar el formato, sabe decodificarlo. De un GIF animado toma el primer
   * fotograma, que es justo lo que §9.3 pide.
   */
  decode: async (file) => {
    const bitmap = await createImageBitmap(file);
    return { width: bitmap.width, height: bitmap.height, source: bitmap };
  },
  encode: (source, width, height, quality) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (context === null) return Promise.resolve(null);

    context.drawImage(source, 0, 0, width, height);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), 'image/webp', quality);
    });
  },
};

/**
 * Convierte una imagen a lo que se sube.
 *
 * **Nunca lanza**: devuelve el motivo. Quien la llama es un editor, y una excepción ahí se convierte
 * en una pantalla en blanco a mitad de preparar una clase.
 */
export async function convertImage(
  file: File,
  options: { readonly usedBytes?: number; readonly env?: ImageEnvironment } = {},
): Promise<ConversionResult> {
  const env = options.env ?? browserEnvironment;

  if (!file.type.startsWith('image/')) return { ok: false, reason: 'images.error.notAnImage' };
  if (file.size > MAX_INPUT_BYTES) return { ok: false, reason: 'images.error.tooLarge' };

  let decoded: Awaited<ReturnType<ImageEnvironment['decode']>>;
  try {
    decoded = await env.decode(file);
  } catch {
    // Un formato que el navegador no sabe decodificar, o un archivo corrupto.
    return { ok: false, reason: 'images.error.decodeFailed' };
  }

  const grande = fit(decoded.width, decoded.height, MAX_EDGE);
  const pequena = fit(decoded.width, decoded.height, THUMB_EDGE);

  const blob = await env.encode(decoded.source, grande.width, grande.height, QUALITY);
  const thumbnail = await env.encode(decoded.source, pequena.width, pequena.height, THUMB_QUALITY);

  if (blob === null || thumbnail === null)
    return { ok: false, reason: 'images.error.decodeFailed' };

  // Por encima de esto, algo ha ido mal: una imagen de 1280 px en WebP no llega a 400 kB salvo que
  // sea ruido puro. Subirla igualmente llenaría la cuota con una sola.
  if (blob.size > MAX_OUTPUT_BYTES) return { ok: false, reason: 'images.error.stillTooLarge' };

  // La cuota se comprueba **después** de convertir, con el tamaño real: rechazar por el tamaño de
  // entrada haría imposible subir una foto de ocho megas que acaba pesando ciento veinte kilobytes.
  const usados = options.usedBytes ?? 0;
  if (usados + blob.size > QUOTA_BYTES) return { ok: false, reason: 'images.error.quotaExceeded' };

  return {
    ok: true,
    image: { blob, thumbnail, width: grande.width, height: grande.height },
  };
}
